/**
 * useSuggestedRules Hook
 *
 * A React hook for managing suggested classification rules via the API.
 * Provides methods for fetching, approving, and rejecting suggested rules.
 */

import { useState, useCallback } from 'react';
import type {
  SuggestedRule,
  SuggestedRulesResponse,
  ApproveRuleResponse,
  RejectRuleResponse,
  PaginationMeta,
} from '@/types/accounting';

export interface UseSuggestedRulesReturn {
  /** Current list of suggested rules */
  suggestedRules: SuggestedRule[];
  /** Pagination metadata */
  pagination: PaginationMeta | null;
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Fetch suggested rules with optional pagination */
  fetchRules: (page?: number) => Promise<SuggestedRulesResponse>;
  /** Approve a suggested rule, optionally with overrides */
  approveRule: (
    id: number,
    overrides?: {
      name?: string;
      patternOverride?: string;
      targetAccountIdOverride?: number;
    }
  ) => Promise<ApproveRuleResponse>;
  /** Reject a suggested rule */
  rejectRule: (id: number) => Promise<RejectRuleResponse>;
  /** Clear any error state */
  clearError: () => void;
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

/**
 * Hook for managing suggested classification rules
 *
 * @example
 * ```typescript
 * const { suggestedRules, loading, error, fetchRules, approveRule, rejectRule } = useSuggestedRules();
 *
 * // Fetch rules on mount
 * useEffect(() => {
 *   fetchRules();
 * }, [fetchRules]);
 *
 * // Approve a rule
 * const handleApprove = async (id: number) => {
 *   await approveRule(id, 'My Custom Rule Name');
 * };
 *
 * // Reject a rule
 * const handleReject = async (id: number) => {
 *   await rejectRule(id);
 * };
 * ```
 */
export function useSuggestedRules(): UseSuggestedRulesReturn {
  const [suggestedRules, setSuggestedRules] = useState<SuggestedRule[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch suggested rules from the API
   */
  const fetchRules = useCallback(async (page = 1): Promise<SuggestedRulesResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/suggested_rules?page=${page}`);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch suggested rules: ${response.statusText}`);
      }

      const data = (await response.json()) as SuggestedRulesResponse;
      setSuggestedRules(data.suggestedRules);
      setPagination(data.pagination);
      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load suggested rules';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve a suggested rule
   */
  const approveRule = useCallback(
    async (
      id: number,
      overrides?: {
        name?: string;
        patternOverride?: string;
        targetAccountIdOverride?: number;
      }
    ): Promise<ApproveRuleResponse> => {
      setLoading(true);
      setError(null);

      try {
        const body: Record<string, string | number> = {};
        if (overrides?.name) {
          body.name = overrides.name;
        }
        if (overrides?.patternOverride) {
          body.pattern_override = overrides.patternOverride;
        }
        if (overrides?.targetAccountIdOverride) {
          body.target_account_id_override = overrides.targetAccountIdOverride;
        }

        const response = await fetch(`/api/accounting/suggested_rules/${id}/approve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
          body: JSON.stringify(body),
        });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Suggested rule not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to approve rule: ${response.statusText}`);
      }

      const data = (await response.json()) as ApproveRuleResponse;

      if (data.success) {
        // Remove the approved rule from the local state
        setSuggestedRules((prev) => prev.filter((r) => r.id !== id));
      }

      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to approve rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reject a suggested rule
   */
  const rejectRule = useCallback(async (id: number): Promise<RejectRuleResponse> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/accounting/suggested_rules/${id}/reject`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          throw new Error('Suggested rule not found');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to reject rule: ${response.statusText}`);
      }

      const data = (await response.json()) as RejectRuleResponse;

      if (data.success) {
        // Remove the rejected rule from the local state
        setSuggestedRules((prev) => prev.filter((r) => r.id !== id));
      }

      return data;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to reject rule';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    suggestedRules,
    pagination,
    loading,
    error,
    fetchRules,
    approveRule,
    rejectRule,
    clearError,
  };
}
